/**
 * Pressure, and what to do when the hardware does not report any.
 *
 * Windows tablet drivers in "mouse mode", trackpads and plain mice all report exactly
 * 0.5 for every sample. Twenty of those in a row means the device has no pressure to
 * give, so the pen switches to varying width by speed instead — automatically and
 * silently, per docs/11-MICRO-PHASES.md step 2.2. The teacher is never told, because
 * there is nothing they could do about it.
 *
 * The result is written back into the stroke as a pressure value, so everything
 * downstream — rendering, saving, replay — sees one kind of stroke and never has to
 * know which device drew it.
 */

/** The value a device sends when it has no pressure sensor. */
export const FLAT_PRESSURE = 0.5;

/** How many identical samples prove the device is not pressure-sensitive. */
export const FLAT_SAMPLES_BEFORE_FALLBACK = 20;

/** Speed, in canvas units per millisecond, at which the fallback stroke is thinnest. */
const FASTEST = 3;

/** Range the fallback maps speed onto. A slow hand presses hard, a fast one skims. */
const FALLBACK_MIN = 0.25;
const FALLBACK_MAX = 0.9;

export interface PressureSample {
  /** Canvas units. */
  readonly x: number;
  readonly y: number;
  /** What the device reported, 0–1. */
  readonly pressure: number;
  /** Milliseconds, from the pointer event. */
  readonly t: number;
}

export interface PressureReader {
  /** The pressure to store for this sample: reported, or derived from speed. */
  read(sample: PressureSample): number;
  /** True once the device has proved it reports no pressure. */
  readonly usingVelocity: boolean;
}

/** Speed between two samples in canvas units per millisecond. */
export function speedBetween(previous: PressureSample, next: PressureSample): number {
  const elapsed = next.t - previous.t;
  if (elapsed <= 0) return 0;
  const dx = next.x - previous.x;
  const dy = next.y - previous.y;
  return Math.hypot(dx, dy) / elapsed;
}

/** Fast strokes come out thin, slow ones thick. Clamped to a sensible range. */
export function velocityPressure(speed: number): number {
  const share = Math.min(Math.max(speed, 0), FASTEST) / FASTEST;
  return FALLBACK_MAX - share * (FALLBACK_MAX - FALLBACK_MIN);
}

/**
 * Watches the samples of one device.
 *
 * The flat-sample count survives across strokes: a mouse does not become
 * pressure-sensitive between two lines, and re-testing every stroke would make the
 * first twenty samples of each one look different from the rest.
 */
export function createPressureReader(): PressureReader {
  let flatRun = 0;
  let fallback = false;
  let previous: PressureSample | null = null;

  return {
    get usingVelocity() {
      return fallback;
    },
    read(sample) {
      if (!fallback) {
        flatRun = sample.pressure === FLAT_PRESSURE ? flatRun + 1 : 0;
        if (flatRun >= FLAT_SAMPLES_BEFORE_FALLBACK) fallback = true;
      }

      const result = fallback
        ? velocityPressure(previous ? speedBetween(previous, sample) : 0)
        : sample.pressure;
      previous = sample;
      return result;
    },
  };
}
